/**
 * DD-3687: the shapes super-admin-backend's historical import answers with.
 *
 * Hand-written, like every other model here — the app has no GraphQL codegen,
 * and these come from REST endpoints in any case.
 */

/**
 * The five files an export is made of, in the order they are shown.
 *
 * The upload type travels as the file's name, which is the live order
 * import's own contract — `formData.append('files', file, 'actions')` — so one
 * dialog pattern serves both imports.
 */
export const HISTORICAL_SOURCE_FILE_KINDS = ['orders', 'actions', 'materials', 'messages', 'attachments'] as const;

export type HistoricalSourceFileKind = (typeof HISTORICAL_SOURCE_FILE_KINDS)[number];

/** Only the orders file is required; an archive may have no messages or files. */
export const REQUIRED_SOURCE_FILE_KIND: HistoricalSourceFileKind = 'orders';

export type HistoricalSourceFiles = Partial<Record<HistoricalSourceFileKind, File>>;

/**
 * What the "Type of import" dropdown shows. Four of the five reuse the order
 * import's own labels, so the two dialogs read the same; only the attachment
 * list is new, and it is named for what it is rather than for the column.
 */
export const SOURCE_FILE_KIND_LABEL_KEYS: Record<HistoricalSourceFileKind, string> = {
    orders: 'orders:ORDERS',
    actions: 'orders:ACTIONS',
    materials: 'orders:MATERIALS',
    messages: 'orders:MESSAGES',
    attachments: 'orders:HISTORICAL_FILES',
};

/** One dropped CSV, before and after its type is chosen. */
export type StagedFile = { file: File; kind?: HistoricalSourceFileKind };

export enum HistoricalImportStatus {
    VALIDATING = 'validating',
    FAILED = 'failed',
    WRITING = 'writing',
    AWAITING_ATTACHMENTS = 'awaiting-attachments',
    COMPLETE = 'complete',
}

export type HistoricalImportFailure = {
    sourceFile: string;
    /** 1-based, so it matches the row number in the operator's own file. */
    row: number;
    reason: string;
};

export type HistoricalImportBatch = {
    _id: string;
    status: HistoricalImportStatus;
    ordersValidated?: number;
    ordersWritten?: number;
    linesWritten?: number;
    messagesWritten?: number;
    attachmentsExpected?: number;
    attachmentsStored?: number;
    /** Capped server-side; `totalFailures` holds the real count. */
    failures?: HistoricalImportFailure[];
    totalFailures?: number;
};

export type StartImportResponse = {
    importBatchId: string;
    status: HistoricalImportStatus;
};

export type AttachmentOutcome = {
    fileName: string;
    outcome: 'stored' | 'skipped' | 'rejected' | 'failed';
    reason?: string;
};

export type UploadAttachmentsResponse = {
    results: AttachmentOutcome[];
    attachmentsStored: number;
    attachmentsExpected: number;
    attachmentsOutstanding: number;
    status: HistoricalImportStatus;
};

/**
 * How many attachments go in one request. The server caps the same number, so
 * raising this alone would start producing 400s.
 */
export const ATTACHMENT_CHUNK_SIZE = 50;

/** How often the dialog asks the server how the run is going. */
export const BATCH_POLL_INTERVAL_MS = 2000;
